import React, { useState, useEffect } from 'react';
import { PlusIcon, PencilIcon, TrashIcon, BadgeCheckIcon, CurrencyDollarIcon, UserGroupIcon, CalendarIcon } from '@heroicons/react/outline';
import FormModal from '../../common/FormModal';
import DeleteConfirmModal from '../../common/DeleteConfirmModal';
import { Switch } from '@headlessui/react';
import toastService from '../../../services/toast';
import courseDiscountsApi from '../../../services/api/discounts.api';
import { usePermissions } from '../../../contexts/PermissionsContext';



const CourseDiscounts = () => {
  const { hasPermission } = usePermissions();
  const [discounts, setDiscounts] = useState([]);
  const [courses, setCourses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingAction, setIsLoadingAction] = useState(false);
  
  // جلب البيانات عند تحميل الصفحة
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const [coursesData, discountsData] = await Promise.all([
          courseDiscountsApi.getCourses(),
          courseDiscountsApi.getAll()
        ]);
        
        // تنسيق بيانات الدورات
        const formattedCourses = Array.isArray(coursesData.data) 
          ? coursesData.data.map(course => ({ id: course.id, name: course.name }))
          : [];
        
        // تنسيق بيانات الخصومات
        const formattedDiscounts = Array.isArray(discountsData.data)
          ? discountsData.data.map(discount => ({
              id: discount.id,
              courseId: discount.course_id,
              courseName: formattedCourses.find(c => c.id === discount.course_id)?.name || 'دورة غير معروفة',
              discountType: discount.discount_type,
              discountValue: discount.discount_value,
              startDate: discount.start_date,
              endDate: discount.end_date,
              maxUsers: discount.max_users,
              usedCount: discount.used_count || 0,
              active: discount.active
            }))
          : [];
        
        setCourses(formattedCourses);
        setDiscounts(formattedDiscounts);
      } catch (error) {
        // Error handling
        toastService.error('حدث خطأ أثناء جلب البيانات');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, []);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedDiscount, setSelectedDiscount] = useState(null);
  const [formData, setFormData] = useState({
    courseId: '',
    courseName: '',
    discountType: 'fixed',
    discountValue: '',
    startDate: '',
    endDate: '',
    maxUsers: '',
    hasMaxUsers: false,
    active: true
  });


  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ar', { 
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  // دالة للتحقق من انتهاء الكوبون
  const isExpired = (endDate) => {
    return new Date(endDate) < new Date();
  };

  const handleAdd = () => {
    setFormData({
      courseId: '',
      courseName: '',
      discountType: 'fixed',
      discountValue: '',
      startDate: '',
      endDate: '',
      maxUsers: '',
      hasMaxUsers: false,
      active: true
    });
    setIsAddModalOpen(true);
  };

  // تحويل التاريخ إلى صيغة yyyy-MM-dd المناسبة لحقول التاريخ في HTML
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleEdit = (discount) => {
    // Get discount data for editing
    setSelectedDiscount(discount);
    setFormData({
      courseId: discount.courseId,
      courseName: discount.courseName,
      discountType: discount.discountType,
      discountValue: discount.discountValue,
      startDate: formatDateForInput(discount.startDate),
      endDate: formatDateForInput(discount.endDate),
      maxUsers: discount.maxUsers || '',
      hasMaxUsers: discount.maxUsers ? true : false,
      active: discount.active
    });
    setIsEditModalOpen(true);
  };

  const handleDelete = (discount) => {
    setSelectedDiscount(discount);
    setIsDeleteModalOpen(true);
  };

  const validateForm = () => {
    const errors = [];

    // Validate course
    if (!formData.courseId) {
      errors.push('يجب اختيار الدورة');
    }

    // Validate discount value
    if (!formData.discountValue) {
      errors.push('يجب إدخال قيمة الخصم');
    } else if (formData.discountValue <= 0) {
      if (formData.discountType === 'percentage') {
        errors.push('يجب أن تكون النسبة بين 1 و 100');
      } else {
        errors.push('يجب أن يكون المبلغ أكبر من 0');
      }
    } else if (formData.discountType === 'percentage' && formData.discountValue > 100) {
      errors.push('يجب أن تكون النسبة بين 1 و 100');
    }

    // Validate dates
    if (!formData.startDate) {
      errors.push('يجب إدخال تاريخ البداية');
    }

    if (!formData.endDate) {
      errors.push('يجب إدخال تاريخ الانتهاء');
    }

    if (formData.startDate && formData.endDate) {
      const startDate = new Date(formData.startDate);
      const endDate = new Date(formData.endDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (startDate < today) {
        errors.push('يجب أن يكون تاريخ البداية اليوم أو بعده');
      }

      if (endDate <= startDate) {
        errors.push('يجب أن يكون تاريخ الانتهاء بعد تاريخ البداية');
      }
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const errors = validateForm();
    if (errors.length > 0) {
      toastService.error(
        <div className="text-right" dir="rtl">
          <p className="font-bold mb-2">يرجى تصحيح الأخطاء التالية:</p>
          <ul className="list-disc mr-5">
            {errors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </div>
      );
      return;
    }

    try {
      setIsLoadingAction(true);
      
      // تحضير البيانات للإرسال إلى API
      const discountData = {
        discount_type: formData.discountType,
        discount_value: formData.discountValue,
        start_date: formData.startDate,
        end_date: formData.endDate,
        max_users: formData.hasMaxUsers ? formData.maxUsers : null,
        active: formData.active
      };

      let response;

      if (selectedDiscount) {
        // تحديث خصم موجود
        response = await courseDiscountsApi.update(
          formData.courseId, 
          selectedDiscount.id, 
          discountData
        );
        
        // تحديث الخصومات في الواجهة
        setDiscounts(discounts.map(d => {
          if (d.id === selectedDiscount.id) {
            return {
              ...d,
              courseId: formData.courseId,
              courseName: courses.find(c => c.id === parseInt(formData.courseId))?.name || 'دورة غير معروفة',
              discountType: formData.discountType,
              discountValue: formData.discountValue,
              startDate: formData.startDate,
              endDate: formData.endDate,
              maxUsers: formData.maxUsers,
              active: formData.active
            };
          }
          return d;
        }));
        
        setIsEditModalOpen(false);
        toastService.success('تم تحديث الخصم بنجاح');
      } else {
        // إنشاء خصم جديد
        response = await courseDiscountsApi.create(formData.courseId, discountData);
        
        // إضافة الخصم الجديد إلى القائمة
        const newDiscount = {
          id: response.data.id,
          courseId: formData.courseId,
          courseName: courses.find(c => c.id === parseInt(formData.courseId))?.name || 'دورة غير معروفة',
          discountType: formData.discountType,
          discountValue: formData.discountValue,
          startDate: formData.startDate,
          endDate: formData.endDate,
          maxUsers: formData.maxUsers,
          usedCount: 0,
          active: formData.active
        };
        
        setDiscounts([...discounts, newDiscount]);
        setIsAddModalOpen(false);
        toastService.success('تم إضافة الخصم بنجاح');
      }

      // إعادة تعيين النموذج
      setFormData({
        courseId: '',
        discountType: 'fixed',
        discountValue: '',
        startDate: '',
        endDate: '',
        maxUsers: '',
        active: true
      });
      setSelectedDiscount(null);
      
    } catch (error) {
      toastService.error('حدث خطأ أثناء حفظ الخصم');
    } finally {
      setIsLoadingAction(false);
    }
  };

  // تغيير حالة تفعيل الخصم
  const handleToggleStatus = async (discount) => {
    try {
      setIsLoadingAction(true);
      await courseDiscountsApi.toggleStatus(discount.courseId, discount.id);
      
      // تحديث الخصومات في الواجهة
      setDiscounts(discounts.map(d => {
        if (d.id === discount.id) {
          return { ...d, active: !d.active };
        }
        return d;
      }));
      
      toastService.success(`تم ${discount.active ? 'تعطيل' : 'تفعيل'} الخصم بنجاح`);
    } catch (error) {
      // Handle toggle error
      toastService.error('حدث خطأ أثناء تغيير حالة الخصم');
    } finally {
      setIsLoadingAction(false);
    }
  };

  const handleConfirmDelete = async () => {
    try {
      setIsLoadingAction(true);
      
      // حذف الخصم من خلال API
      await courseDiscountsApi.delete(selectedDiscount.courseId, selectedDiscount.id);
      
      // حذف الخصم من القائمة في الواجهة
      setDiscounts(discounts.filter(d => d.id !== selectedDiscount.id));
      setIsDeleteModalOpen(false);
      setSelectedDiscount(null);
      toastService.success('تم حذف الخصم بنجاح');
    } catch (error) {
      // Handle delete error
      toastService.error('حدث خطأ أثناء حذف الخصم');
    } finally {
      setIsLoadingAction(false);
    }
  };

  const renderForm = () => (
    <div className="space-y-4">
      {/* الدورة */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">الدورة</label>
        <select
          name="courseId"
          value={formData.courseId}
          onChange={e => {
            const course = courses.find(c => c.id === Number(e.target.value));
            setFormData({ 
              ...formData, 
              courseId: Number(e.target.value),
              courseName: course ? course.name : ''
            });
          }}
          className="input-base w-full"
        >
          <option value="">اختر الدورة</option>
          {courses.map(course => (
            <option key={course.id} value={course.id}>
              {course.name}
            </option>
          ))}
        </select>
      </div>

      {/* نوع الخصم وقيمته */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">نوع الخصم</label>
          <select
            value={formData.discountType}
            onChange={e => setFormData({ ...formData, discountType: e.target.value })}
            className="input-base w-full"
          >
            <option value="percentage">نسبة مئوية</option>
            <option value="fixed">مبلغ ثابت</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">قيمة الخصم</label>
          <input
            type="number"
            min="0"
            max={formData.discountType === 'percentage' ? "100" : undefined}
            value={formData.discountValue}
            onChange={e => setFormData({ ...formData, discountValue: e.target.value })}
            className="input-base w-full"
            placeholder={formData.discountType === 'percentage' ? "أدخل النسبة المئوية" : "أدخل المبلغ"}
          />
        </div>
      </div>

      {/* تاريخ البداية والنهاية */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">تاريخ البداية</label>
          <input
            type="date"
            value={formData.startDate}
            onChange={e => setFormData({ ...formData, startDate: e.target.value })}
            className="input-base w-full"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">تاريخ الانتهاء</label>
          <input
            type="date"
            value={formData.endDate}
            onChange={e => setFormData({ ...formData, endDate: e.target.value })}
            className="input-base w-full"
          />
        </div>
      </div>

      {/* الحد الأقصى للمستخدمين + السويتش */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">الحد الأقصى للمستخدمين</label>
          <input
            type="number"
            min="1"
            value={formData.maxUsers}
            onChange={e => setFormData({ ...formData, maxUsers: e.target.value })}
            className="input-base w-full"
            placeholder="أدخل الحد الأقصى للمستخدمين"
            disabled={!formData.hasMaxUsers}
          />
        </div>

        <div className="flex items-end mb-[6px]">
          <div className="flex items-center gap-2">
            <Switch
              checked={formData.hasMaxUsers}
              onChange={(checked) => setFormData({ ...formData, hasMaxUsers: checked, maxUsers: checked ? formData.maxUsers || '1' : '' })}
              className={`switch-rtl ${formData.hasMaxUsers ? 'bg-blue-500 dark:bg-blue-600' : 'bg-gray-400 dark:bg-gray-600'} relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none`}
            >
              <span
                className={`${formData.hasMaxUsers ? '-translate-x-6' : '-translate-x-1'} inline-block h-5 w-5 transform rounded-full bg-white transition-transform`}
              />
            </Switch>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {formData.hasMaxUsers ? 'فعال' : 'غير فعال'}
            </label>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-6">
      {/* بطاقة العنوان */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg mb-6">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <h2 className="text-lg font-medium text-gray-900 dark:text-white">خصومات الدورات</h2>
          <div className="flex gap-4">
            {hasPermission && hasPermission('create_course_discount') && (
              <div className="relative group">
                <PlusIcon 
                  className="h-6 w-6 text-blue-600 hover:text-blue-700 dark:text-blue-500 dark:hover:text-blue-400 cursor-pointer transition-colors duration-200"
                  onClick={handleAdd}
                />
                <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-700 delay-0 -left-4 -translate-x-1/2 mt-2 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-50 transition-all duration-75">
                  إضافة خصم جديد
                </div>
              </div>
            )}
          </div>
        </div>

        {/* محتوى البطاقة */}
        <div className="space-y-4 p-6">
          {isLoading ? (
            /* شكلتون للتحميل */
            [...Array(3)].map((_, index) => (
              <div key={index} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm animate-pulse">
                <div className="p-4">
                  <div className="flex items-start gap-6 mb-4">
                    <div className="flex-1">
                      <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-2/3 mb-2"></div>
                    </div>
                    <div className="flex gap-2">
                      <div className="h-5 w-5 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
                      <div className="h-5 w-5 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-4 justify-between">
                    <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
                    <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
                    <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            /* محتوى الخصومات */
            discounts.map((discount) => (
              <div key={discount.id} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow duration-200">
                <div className="p-4">
                  {/* رأس البطاقة */}
                  <div className="flex items-start gap-6 mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center gap-2">
                        {discount.courseName}
                        <BadgeCheckIcon className={`h-5 w-5 ${
                          isExpired(discount.endDate)
                            ? 'text-red-500'
                            : !discount.active 
                              ? 'text-gray-400 dark:text-gray-600'
                              : 'text-green-500'
                        }`} />
                      </h3>
                    </div>
                  <div className="flex items-center gap-3 self-center">
                    {hasPermission && hasPermission('edit_course_discount') && (
                      <div className="relative group">
                        <PencilIcon 
                          className="h-5 w-5 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 cursor-pointer transition-colors duration-200"
                          onClick={() => handleEdit(discount)}
                        />
                        <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-700 delay-0 -translate-x-1/2 left-1/2 mt-2 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-50 transition-all duration-75">
                          تعديل الخصم
                        </div>
                      </div>
                    )}
                    {hasPermission && hasPermission('delete_course_discount') && (
                      <div className="relative group">
                        <TrashIcon 
                          className="h-5 w-5 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 cursor-pointer transition-colors duration-200"
                          onClick={() => handleDelete(discount)}
                        />
                        <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-700 delay-0 -translate-x-1/2 left-1/2 mt-2 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-50 transition-all duration-75">
                          حذف الخصم
                        </div>
                      </div>
                    )}

                  </div>
                </div>

                {/* تفاصيل الخصم */}
                <div className="flex flex-wrap items-center gap-6">
                  <div className="flex items-center gap-2 text-sm">
                    <div className="flex items-center gap-1.5 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 px-2.5 py-1.5 rounded-md">
                      <CurrencyDollarIcon className="h-4 w-4" />
                      <span>
                        {discount.discountType === 'percentage' ? `${discount.discountValue}%` : <>
                          {discount.discountValue} <span style={{ fontFamily: 'saudi_riyal' }}>&#xE900;</span>
                        </>}
                      </span>
                    </div>
                    {discount.maxUsers && (
                      <div className="flex items-center gap-1.5 bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 px-2.5 py-1.5 rounded-md">
                        <UserGroupIcon className="h-4 w-4" />
                        <span>{discount.usedCount}/{discount.maxUsers}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-6 text-sm text-gray-600 dark:text-gray-400">
                    <div className="flex items-center gap-1.5">
                      <CalendarIcon className="h-4 w-4" />
                      <span>من: {formatDate(discount.startDate)}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <CalendarIcon className="h-4 w-4" />
                      <span>إلى: {formatDate(discount.endDate)}</span>
                    </div>
                  </div>

                  {/* شريط التقدم للاستخدام */}
                  {discount.maxUsers && (
                    <div className="flex-1 flex items-center gap-4">
                      <div className="text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">
                        نسبة الاستخدام: {Math.round((discount.usedCount / discount.maxUsers) * 100)}%
                      </div>
                      <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div 
                          className="bg-blue-600 dark:bg-blue-500 rounded-full h-2 transition-all duration-300"
                          style={{ width: `${(discount.usedCount / discount.maxUsers) * 100}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            ))
          )}
        </div>
      </div>

      {/* مودال الإضافة */}
      <FormModal
        isOpen={isAddModalOpen}
        onClose={() => {
          setIsAddModalOpen(false);
          setSelectedDiscount(null);
        }}
        onSubmit={handleSubmit}
        title="إضافة خصم جديد"
        submitText="إضافة"
        cancelText="إلغاء"
        size="md"
      >
        {renderForm()}
      </FormModal>

      {/* مودال التعديل */}
      <FormModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedDiscount(null);
        }}
        onSubmit={handleSubmit}
        title="تعديل الخصم"
        submitText="تحديث"
        cancelText="إلغاء"
        size="md"
      >
        {renderForm()}
      </FormModal>

      {/* مودال الحذف */}
      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        itemType="الخصم"
        itemTitle={selectedDiscount?.courseName}
      />
    </div>
  );
};

export default CourseDiscounts;
